import axios from 'axios';
import BASE_URL from '@rti/shared/src/services/baseUrl';
import type {
  PEInventoryMetricsItem,
  PEProductionCapacityItem,
  PEMarketsItem,
  PERatesItem,
  USITCTradeItem,
  FeedstockForecastItem,
} from '@/types/pe.types';

const SYNAPSE_BASE = '/gateway/synapse';

function buildParams(obj: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, value);
    }
  }
  const str = params.toString();
  return str ? `?${str}` : '';
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const getPEInventoryMetrics = async (
  resinType: 'pe' | 'hdpe' | 'ldpe' | 'lldpe' = 'pe',
  dateFrom?: string,
  dateTo?: string
): Promise<PEInventoryMetricsItem[]> => {
  const qs = buildParams({ resin_type: resinType, date_from: dateFrom, date_to: dateTo });
  const response = await axios.get(
    `${BASE_URL}${SYNAPSE_BASE}/m/pe/inventory-metrics${qs}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getPEProductionCapacity = async (
  dateFrom?: string,
  dateTo?: string
): Promise<PEProductionCapacityItem[]> => {
  const qs = buildParams({ date_from: dateFrom, date_to: dateTo });
  const response = await axios.get(
    `${BASE_URL}${SYNAPSE_BASE}/m/pe/production-capacity${qs}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getPEMarkets = async (
  resinType: 'hdpe' | 'ldpe' | 'lldpe' = 'hdpe',
  dateFrom?: string,
  dateTo?: string
): Promise<PEMarketsItem[]> => {
  const qs = buildParams({ date_from: dateFrom, date_to: dateTo });
  const response = await axios.get(
    `${BASE_URL}${SYNAPSE_BASE}/m/pe/markets/${resinType}${qs}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getPERates = async (
  dateFrom?: string,
  dateTo?: string
): Promise<PERatesItem[]> => {
  const qs = buildParams({ date_from: dateFrom, date_to: dateTo });
  const response = await axios.get(
    `${BASE_URL}${SYNAPSE_BASE}/m/pe/rates${qs}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getUSITCPETrade = async (
  dateFrom?: string,
  dateTo?: string,
  tradeDirection: 'import' | 'export' = 'import'
): Promise<USITCTradeItem[]> => {
  const qs = buildParams({ date_from: dateFrom, date_to: dateTo, trade_direction: tradeDirection });
  const response = await axios.get(
    `${BASE_URL}${SYNAPSE_BASE}/m/usitc/pe-trade${qs}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getPEFeedstockForecasts = async (
  dateFrom?: string,
  dateTo?: string
): Promise<FeedstockForecastItem[]> => {
  const qs = buildParams({ date_from: dateFrom, date_to: dateTo });
  const response = await axios.get(
    `${BASE_URL}${SYNAPSE_BASE}/m/pe/feedstock-forecasts${qs}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getVAccPE = async (): Promise<any[]> => {
  const response = await axios.get(
    `${BASE_URL}${SYNAPSE_BASE}/m/v_acc_pe`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getVAccPEFinal = async (): Promise<any[]> => {
  const response = await axios.get(
    `${BASE_URL}${SYNAPSE_BASE}/m/v_acc_pe_final`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getFairPriceIndex = async (
  dateFrom?: string,
  dateTo?: string
): Promise<any[]> => {
  const qs = buildParams({ date_from: dateFrom, date_to: dateTo });
  const response = await axios.get(
    `${BASE_URL}${SYNAPSE_BASE}/m/forecasts/fair-price-index/date-range${qs}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getRtiPetchem = async (): Promise<any[]> => {
  const response = await axios.get(
    `${BASE_URL}${SYNAPSE_BASE}/d/rti-petchem`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};
